import { useParams } from '@tanstack/react-router'
import { Maximize2, Minimize2, QrCode } from 'lucide-react'
import { domAnimation, LazyMotion, m } from 'motion/react'
import { useCallback, useEffect, useState } from 'react'

import { LIFFQR } from '@/components/common/LIFFQR'
import { cn } from '@/lib/utils'

export const CheckinSignage = () => {
  const { eventId } = useParams({
    from: '/e/$eventId/_display/signage/checkin',
  })
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isFullscreenSupported, setIsFullscreenSupported] = useState(false)

  useEffect(() => {
    const supported = !!(
      document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.mozFullScreenEnabled ||
      document.msFullscreenEnabled
    )
    setIsFullscreenSupported(supported)
  }, [])

  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      const element = document.documentElement
      const requestMethod =
        element.requestFullscreen ||
        element.webkitRequestFullscreen ||
        element.mozRequestFullScreen ||
        element.msRequestFullscreen

      if (requestMethod) {
        await requestMethod.call(element)
      }
    } else {
      const exitMethod =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen

      if (exitMethod) {
        await exitMethod.call(document)
      }
    }
  }, [])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!(document.fullscreenElement || document.webkitFullscreenElement),
      )
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key.toLowerCase() === 'f' &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        toggleFullscreen()
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange,
      )
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [toggleFullscreen])

  return (
    <LazyMotion features={domAnimation}>
      <div className="bg-background text-forest-light relative flex min-h-screen flex-col items-center justify-center overflow-hidden font-sans">
        {/* Background Orbs */}
        <div className="absolute top-0 left-0 size-full overflow-hidden">
          <m.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
              x: [0, 50, 0],
              y: [0, 30, 0],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="bg-blush-soft absolute -top-24 -left-24 size-96 rounded-full blur-[100px]"
          />
          <m.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.4, 0.2],
              x: [0, -40, 0],
              y: [0, 50, 0],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2,
            }}
            className="bg-sage-light/20 absolute top-1/2 -right-24 size-125 -translate-y-1/2 rounded-full blur-[120px]"
          />
          <m.div
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.1, 0.3, 0.1],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 5,
            }}
            className="bg-rose-dark/10 absolute -bottom-48 left-1/4 size-150 rounded-full blur-[150px]"
          />
        </div>

        <div className="relative z-10 flex min-h-screen flex-col items-center justify-center gap-6 px-6 py-8 text-center md:gap-12 md:py-12">
          {/* Header Section */}
          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-2 md:space-y-4"
          >
            <div className="bg-forest-light/5 mx-auto flex w-fit items-center gap-2 rounded-full px-4 py-1.5 backdrop-blur-md md:gap-3 md:px-6 md:py-2">
              <QrCode className="text-rose-dark size-5 md:size-6" />
              <span className="text-forest-light/70 text-[10px] font-semibold tracking-[0.2em] uppercase md:text-sm">
                Welcome to our event
              </span>
            </div>
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl md:text-7xl">
              <span className="from-forest-light via-forest-light/90 to-forest-light/60 bg-linear-to-r bg-clip-text text-transparent">
                Scan to Check-in
              </span>
            </h1>
            <p className="text-forest-light/50 text-base font-medium md:text-2xl">
              สแกนเพื่อลงทะเบียนเข้างาน
            </p>
          </m.div>

          {/* QR Code Section */}
          <m.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="group relative"
          >
            {/* Animated Glow behind QR */}
            <div className="from-blush-soft to-sage-light/30 absolute -inset-4 rounded-[40px] bg-linear-to-tr opacity-75 blur-2xl transition duration-500 group-hover:opacity-100" />

            <div className="border-forest-light/5 relative rounded-[32px] border bg-white p-6 shadow-2xl backdrop-blur-xl md:p-10">
              <div className="hidden md:block">
                <LIFFQR
                  path={`/e/${eventId}/checkin`}
                  size={256}
                  color="#344C3D"
                />
              </div>
              <div className="md:hidden">
                <LIFFQR
                  path={`/e/${eventId}/checkin`}
                  size={180}
                  color="#344C3D"
                />
              </div>

              {/* Corner Accents */}
              <div className="border-rose-dark absolute -top-1 -left-1 size-8 rounded-tl-3xl border-t-4 border-l-4" />
              <div className="border-rose-dark absolute -top-1 -right-1 size-8 rounded-tr-3xl border-t-4 border-r-4" />
              <div className="border-rose-dark absolute -bottom-1 -left-1 size-8 rounded-bl-3xl border-b-4 border-l-4" />
              <div className="border-rose-dark absolute -right-1 -bottom-1 size-8 rounded-br-3xl border-r-4 border-b-4" />
            </div>
          </m.div>

          {/* Instructions */}
          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col gap-4 md:grid md:max-w-3xl md:grid-cols-3 md:gap-8"
          >
            <InstructionStep
              number="1"
              title="Open Camera"
              desc="เปิดแอป LINE"
              mobileHorizontal
            />
            <InstructionStep
              number="2"
              title="Scan QR"
              desc="สแกนรหัสเช็กอิน"
              mobileHorizontal
            />
            <InstructionStep
              number="3"
              title="Done!"
              desc="พร้อมร่วมงาน"
              mobileHorizontal
            />
          </m.div>
        </div>

        {/* Fullscreen Toggle Button */}
        {isFullscreenSupported && (
          <m.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            onClick={toggleFullscreen}
            className="border-forest-light/10 text-forest-light/50 hover:text-forest-light absolute top-8 right-8 flex size-12 items-center justify-center rounded-2xl border bg-white/50 backdrop-blur-xl transition-all hover:bg-white/80 active:scale-95"
            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            {isFullscreen ? (
              <Minimize2 className="size-6" />
            ) : (
              <Maximize2 className="size-6" />
            )}
          </m.button>
        )}
      </div>
    </LazyMotion>
  )
}

const InstructionStep = ({
  number,
  title,
  desc,
  mobileHorizontal = false,
}: {
  number: string
  title: string
  desc: string
  mobileHorizontal?: boolean
}) => (
  <div
    className={cn(
      'flex items-center gap-4 text-left md:flex-col md:gap-3 md:text-center',
      mobileHorizontal ? 'flex-row' : 'flex-col',
    )}
  >
    <div className="bg-sage-light/10 text-sage-light ring-sage-light/20 flex size-10 shrink-0 items-center justify-center rounded-full text-lg font-bold ring-1">
      {number}
    </div>
    <div className="space-y-0.5 md:space-y-1">
      <h3 className="text-base font-bold md:text-lg">{title}</h3>
      <p className="text-forest-light/60 text-xs md:text-sm">{desc}</p>
    </div>
  </div>
)
